import { css } from '@emotion/css';
import { multiSelectStyle } from 'Shared/forms/Inputs'
import { LabelOnLeftWrap, labelStyle, noteStyle } from 'Shared/forms/Wrap'
import type {StylesLayout} from 'Shared/model/Components';
import { optionList } from 'Shared/backend/widgets/ComponentOptions';
import { useBuild, useSite } from 'Shared/SiteProvider';
import {VenueSettingsFactory} from 'Shared/VenueSettingsFactory';
import { For, Show, createMemo, createSignal, onMount, onCleanup } from 'solid-js';
import { IntegratedSelectorAndComponent } from 'Shared/backend/widgets/IntegratedSelectorAndComponent';
import { Dynamic } from 'solid-js/web';
import { StoreProvider, useStore } from 'Shared/StoreProvider';
import { dbPatchField } from 'Shared/forms/BaseOperations';
import { useActiveStyles, ActiveStylesProvider } from 'Shared/backend/ActiveStylesContext';


export const paneIndentStyle = () => css({
	paddingLeft: '4em'
});

/*
	NOTE 
	Style components could be viewed as roughly representing a kind of implicit "widget", and
	it may help to view them as such. The styles are often going to be reusable.

	While widgets could be formally separated out for reuse, this probably doesn't work... different 
	widgets need to contain different content (eg different text, different images) which is easier accessed in situ. 
	Ultimately this is what the facades would be for. The proposal is to apply facades at the section level - otherwise deciding
	what is in a widget is likely to be rather arbitrary. 
	Also just selecting between prebaked sections is easiest for users.

	NOTE while styles could just be applied at the section level, this is probably not fine-grain enough.
	For example, consider a slider with overlays that are styled differently.

	NOTE while styles could be applied directly to components, this has its problems.
	Firsly the user is presented with many opportunties to apply styling, which we don't want - we want them to use themes and use defaults.
	Secondly it may appear as if just the component itself it being styled, where in truth the whole subtree is.

	TODO probably remove the Text component classes 
*/

export function Styles(P:StylesLayout)
{
	const sw = useStore();
	const siteTheme = (new VenueSettingsFactory(useBuild())).createSettings(useSite().key).siteTheme();
	const options = optionList([],['popup']);

	const styleDefinitions = () => siteTheme.styles;

	//TODO create parallax class (apply here? or in overlay? or image? Dont know which image, so probably needs to be in overlay)

	//TODO add ignore page margins option

	//TODO add full phone margins (probably apply to Image component directly? Need to only affect images that haven't changed their margins)


	const detailsStyle = css({
		display: 'flex',
		flexDirection: 'column',
		gap: '0.8em',
	});

	const parametersStyle = css({
		display: 'flex',
		flexDirection: 'column',
		gap: '1em',
		justifyContent: 'space-between',
	});

	const innerStyle = css({
		paddingLeft: '1em',
		borderLeft:'solid 2px #ccc'
	});

	const createParams = (previous,field,value) => ({
		...previous,
		[field]: value
	});

	/* Compute which styles are active from ancestors, and which will be active for our children. */
	const parentActiveStyles = useActiveStyles();

	const storePos = () => {
		let pos: any = sw.store;
		for (const part of (sw.location ?? [])) pos = pos?.[part];
		return pos;
	};

	const ownRemoveStyles = createMemo(() => (storePos()?.removeStyles ?? []) as string[]);
	const ownStyleNames = createMemo(() =>
		((storePos()?.styles ?? []) as any[]).map(s => s.type ?? s)
	);

	/* Active styles we will pass down to our children. */
	const ownActiveStyles = createMemo(() => [
		...parentActiveStyles().filter(s => !ownRemoveStyles().includes(s)),
		...ownStyleNames(),
	]);


	return (
		<div class={detailsStyle}>
			<div class={noteStyle()}>
				This layout allows a component to be wrapped in space and borders, allows filters to be applied it, etc.
			</div>

			<LabelOnLeftWrap label='Styles'>
				<StylesField styleDefinitions={styleDefinitions()} />
			</LabelOnLeftWrap>

{/* 
	TODO MIGRATE Chris Settings files but NOT DB
	TODO cf add default values? 
*/}

			<For each={P.styles}>{(style,index) => <div>
				<Show when={siteTheme.styles[style.type]?.ui}>
					<div class={parametersStyle}>
						<label class={labelStyle()}>{style.type}:</label>

						<div class={innerStyle}>
							<StoreProvider {...sw} location={[...sw.location,'styles',index(),'params']} prepare={createParams}>
								<Dynamic component={siteTheme.styles[style.type].ui} />
							</StoreProvider>
						</div>
					</div>
				</Show>
			</div>}</For>

			<ActiveStylesProvider activeStyles={ownActiveStyles}>
				<IntegratedSelectorAndComponent {...P.content} label='Component' contentFieldName='content' options={options} required />
			</ActiveStylesProvider>


			{/* TODO 
				Add a "custom style editor".
				Allow a "selection tree" to be auto-generated.
				Allow nodes to be styled. 
				SEE StyleEditor.tsx for existing code !!! See additonal notes there!!
			*/}
		</div>
	);
}

export function StylesField(P: { styleDefinitions: Record<string, any>, field?: string })
{
	const sw = useStore();
	const field = P.field ?? 'styles';

	const grouped = createMemo(() => {
		const groups: Record<string, string[]> = {};
		for (const [name, def] of Object.entries(P.styleDefinitions)) {
			const cat = (def as any).category ?? 'other';
			if (!groups[cat]) groups[cat] = [];
			groups[cat].push(name);
		}
		return groups;
	});

	const hasOther = createMemo(() => (grouped()['other']?.length ?? 0) > 0);

	const namedCategories = createMemo(() =>
		Object.keys(grouped()).filter(cat => cat !== 'other')
	);

	const categoryLabel = (cat: string) =>
		cat.charAt(0).toUpperCase() + cat.slice(1);

	/* Navigate sw.store directly — getValue() calls useStore() internally which only works in
	   reactive contexts, not async event handlers. */
	const storePos = () => {
		let pos: any = sw.store;
		for (const part of (sw.location ?? []))
			pos = pos?.[part];
		return pos;
	};

	const value = createMemo(() => {
		const raw = (storePos()?.[field] ?? []) as any[];
		return raw.filter(item => P.styleDefinitions[item.type]).map(item => item.type);
	});

	const isSelected = (styleName: string) => value().includes(styleName);

	const currentRaw = () => (storePos()?.[field] ?? []) as any[];

	const toggleStyle = async (styleName: string, e: MouseEvent) => {
		e.stopPropagation();
		const current = currentRaw();
		const newStyles = isSelected(styleName)
			? current.filter((s: any) => s.type !== styleName)
			: [...current, { type: styleName, params: {} }];
		await dbPatchField(sw, field, newStyles);
	};

	const removeStyle = async (styleName: string, e: MouseEvent) => {
		e.stopPropagation();
		const current = currentRaw();
		const newStyles = current.filter((s: any) => s.type !== styleName);
		await dbPatchField(sw, field, newStyles);
	};

	const labelForStyle = (styleName: string) => {
		const def = P.styleDefinitions[styleName];
		if (def?.category)
			return `${categoryLabel(def.category)} > ${styleName}`;
		return styleName;
	};

	const [isOpen, setIsOpen] = createSignal(false);
	const [expandedCategories, setExpandedCategories] = createSignal<Set<string>>(new Set());
	const [tooltip, setTooltip] = createSignal<{name: string, top: number, left: number} | null>(null);

	const toggleCategory = (cat: string, e: MouseEvent) => {
		e.stopPropagation();
		setExpandedCategories(prev => {
			const next = new Set(prev);
			if (next.has(cat)) next.delete(cat); else next.add(cat);
			return next;
		});
	};

	let containerRef: HTMLDivElement;

	onMount(() => {
		const handler = (e: MouseEvent) => {
			if (!containerRef?.contains(e.target as Node))
				setIsOpen(false);
		};
		document.addEventListener('mousedown', handler);
		onCleanup(() => document.removeEventListener('mousedown', handler));
	});

	const controlStyle = css(multiSelectStyle(), {
		cursor: 'pointer',
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: '0.25rem',
		minHeight: '2em',
	});

	const dropdownStyle = css({
		backgroundColor: 'white',
		borderRadius: '0.125rem',
		marginTop: '0.25rem',
		maxHeight: '50vh',
		width: 300,
		overflowY: 'auto',
		padding: '0.5rem 0',
		position: 'absolute',
		boxShadow: '0 10px 15px -3px rgb(0 0 0/0.1), 0 4px 6px -4px rgb(0 0 0/0.1)',
		zIndex: 100,
		fontSize: 12,
	});

	const tagStyle = css({
		backgroundColor: 'rgb(243, 244, 246)',
		borderRadius: '0.25rem',
		display: 'flex',
		alignItems: 'center',
		fontSize: '85%',
		paddingLeft: '4px',
		paddingRight: '2px',
		gap: '2px',
	});

	const removeBtn = css({
		cursor: 'pointer',
		padding: '0 2px',
		'&:hover': { opacity: 0.6 },
	});

	const categoryRowStyle = css({
		cursor: 'default',
		padding: '0.4rem 1rem',
		userSelect: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: '0.4em',
		fontWeight: 'bold',
		'&:hover': { backgroundColor: 'rgb(243, 244, 246)' },
	});

	const styleRowStyle = css({
		cursor: 'default',
		padding: '0.3rem 1rem 0.3rem 2.2rem',
		userSelect: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: '0.5em',
		'&:hover': { backgroundColor: 'rgb(229, 231, 235)' },
	});

	const explanationTooltipStyle = css({
		position: 'fixed',
		marginLeft: '0.5rem',
		backgroundColor: '#333',
		color: 'white',
		borderRadius: '0.25rem',
		padding: '0.4rem 0.6rem',
		fontSize: 11,
		lineHeight: 1.4,
		whiteSpace: 'normal',
		width: 200,
		zIndex: 200,
		pointerEvents: 'none',
		transform: 'translateY(-50%)',
	});

	const renderCategory = (cat: string) => (
		<div>
			<div class={categoryRowStyle} onClick={e => toggleCategory(cat, e)}>
				<span>{expandedCategories().has(cat) ? '▾' : '▸'}</span>
				<span>{cat === 'other' ? 'Other' : categoryLabel(cat)}</span>
			</div>
			<Show when={expandedCategories().has(cat)}>
				<For each={grouped()[cat]}>{styleName =>
					<div
						class={styleRowStyle}
						onClick={e => toggleStyle(styleName, e)}
						onMouseEnter={e => {
							const explanation = P.styleDefinitions[styleName]?.explanation;
							if (!explanation) return;
							const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
							setTooltip({ name: styleName, top: rect.top + rect.height / 2, left: rect.right });
						}}
						onMouseLeave={() => setTooltip(null)}
					>
						<span>{isSelected(styleName) ? '☑' : '☐'}</span>
						<span>{styleName}</span>
					</div>
				}</For>
			</Show>
		</div>
	);

	return (
		<div ref={containerRef!} style={{ position: 'relative' }}>
			<div class={controlStyle} onClick={() => setIsOpen(o => !o)}>
				<For each={value()}>{name =>
					<span class={tagStyle}>
						<span>{labelForStyle(name)}</span>
						<span class={removeBtn} onClick={e => removeStyle(name, e)}>✕</span>
					</span>
				}</For>
				<span style={{ 'margin-left': 'auto', 'padding-left': '0.25rem' }}>▾</span>
			</div>
			<Show when={isOpen()}>
				<div class={dropdownStyle}>
					<For each={namedCategories()}>{cat => renderCategory(cat)}</For>
					<Show when={hasOther()}>{renderCategory('other')}</Show>
				</div>
			</Show>
			<Show when={tooltip()}>
				<div
					class={explanationTooltipStyle}
					style={{ top: `${tooltip()!.top}px`, left: `${tooltip()!.left}px` }}
				>
					{P.styleDefinitions[tooltip()!.name]?.explanation}
				</div>
			</Show>
		</div>
	);
}

